<template>
  <div id="app" class="app-layout">
    <div class="app-main">
      <header class="app-header">
        <div class="title-row">
          <h1 class="game-title">
            <span class="title-brand">
              <span class="title-cn">{{ t('title.cn') }}</span>
              <span class="title-sep" aria-hidden="true">·</span>
              <span class="title-en">{{ t('title.en') }}</span>
            </span>
            <button
              type="button"
              class="help-q"
              :aria-label="t('help.aboutAria')"
              title="?"
              @click="aboutOpen = true"
            >?</button>
            <span class="title-version">{{ t('meta.versionLabel') }}</span>
          </h1>
          <select
            id="locale-select"
            class="lang-select title-lang-select"
            :value="locale"
            @change="onLocaleChange($event)"
          >
            <option
              v-for="opt in localeOptions"
              :key="opt.code"
              :value="opt.code"
            >
              {{ opt.native }}
            </option>
          </select>
        </div>
      </header>

      <Teleport to="body">
        <div
          v-if="aboutOpen"
          class="about-overlay"
          role="presentation"
          @click.self="aboutOpen = false"
        >
          <div
            class="about-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="about-h"
            @keydown.escape="aboutOpen = false"
          >
            <h2 id="about-h" class="about-h">{{ t('about.title') }}</h2>
            <p class="about-body">{{ t('about.body') }}</p>
            <button type="button" class="btn about-close" @click="aboutOpen = false">
              {{ t('dialog.close') }}
            </button>
          </div>
        </div>
      </Teleport>

      <div class="controls-row">
        <ModeSwitch
          class="controls-row__modes"
          :current-mode="gameMode"
          @mode-change="switchMode"
        />
        <label class="assist-toggle">
          <input
            v-model="showMoveAssist"
            type="checkbox"
            class="assist-toggle__input"
          >
          <span>{{ t('mode.moveAssist') }}</span>
        </label>
      </div>

      <!-- 计分板（两侧为玩家名称输入） -->
      <div class="board-width-line">
        <Scoreboard
          :scores="scores"
          :game-mode="gameMode"
          :current-player="currentPlayer"
          :game-over="gameOver"
          :winner="winner"
          :black-name="playerNameBlack"
          :white-name="playerNameWhite"
          @update:black-name="playerNameBlack = $event"
          @update:white-name="playerNameWhite = $event"
        />
      </div>

      <!-- 状态信息（固定占位，避免复盘后布局跳动） -->
      <div class="status-block board-width-line">
        <div class="status-text">
          <template v-if="!gameOver">
            <span class="highlight">{{ statusBannerRich.beforeName }}</span>
            <span class="status-emph">{{ statusBannerRich.name }}</span>
            <span class="highlight">{{ statusBannerRich.between }}</span>
            <span class="status-emph">{{ statusBannerRich.sec }}</span>
            <span class="highlight">{{ statusBannerRich.afterSec }}</span>
          </template>
        </div>
        <div class="skip-message">
          <span v-if="skipMessage">{{ skipMessage }}</span>
        </div>
        <div
          v-if="gameOver && moveLog.length"
          class="status-replay-nav"
          role="group"
          :aria-label="t('side.replayNav')"
        >
          <button
            type="button"
            class="status-replay-btn"
            :disabled="replayCursor <= 0"
            :aria-label="t('side.prevStep')"
            :title="t('side.prevStep')"
            @click="onReplayPrev"
          >
            ←
          </button>
          <button
            type="button"
            class="status-replay-btn"
            :disabled="replayCursor >= moveLog.length"
            :aria-label="t('side.nextStep')"
            :title="t('side.nextStep')"
            @click="onReplayNext"
          >
            →
          </button>
        </div>
      </div>

      <!-- 棋盘 + 右侧对局进程（等高，进程区可滚动） -->
      <div class="board-row">
        <div ref="boardWrapRef" class="board-wrap">
          <Board
            :board="displayBoard"
            :piece-metas="displayPieceMetas"
            :valid-move-set="validMoveSet"
            :move-gain-map="moveAssistMap"
            :show-move-gain="showMoveAssist"
            :last-move-index="displayLastMoveIndex"
            :can-interact="canInteract"
            :game-over="gameOver"
            @cell-click="handleCellClick"
          />
        </div>
        <div
          class="side-track"
          :class="{ 'side-track--mobile': isNarrowLayout }"
          :style="sideTrackStyle"
        >
          <GameSidePanel
            :move-log="moveLog"
            :game-over="gameOver"
            :replay-cursor="replayCursor"
            :scores="scores"
            :result-title="resultTitle"
            :black-display-name="displayNameBlack"
            :white-display-name="displayNameWhite"
            :local-saves="localSaves"
            @play-again="initGame"
            @replay-step="setReplayStep"
            @export-game="downloadGameRecord"
            @import-game="onPickImportFile"
            @save-local="onSaveLocal"
            @load-local-save="onLoadLocalSave"
            @remove-local-save="onRemoveLocalSave"
          />
        </div>
      </div>
      <input
        ref="importFileInput"
        type="file"
        class="sr-only"
        accept="application/json,.json"
        @change="onImportGameFile"
      />
    </div>
    <footer class="app-footer">
      <small>Copyright &copy; {{ new Date().getFullYear() }} Kyle Tang. All rights reserved.</small>
    </footer>
  </div>
</template>

<script setup>
import {
  watch,
  onMounted,
  onUnmounted,
  ref,
  computed,
  nextTick
} from 'vue'
import { useGameState } from './composables'
import { Board, Scoreboard, ModeSwitch, GameSidePanel } from './components'
import { locale, localeOptions, setLocale, t } from '@/i18n/index.js'

const boardWrapRef = ref(null)
const importFileInput = ref(null)
const aboutOpen = ref(false)
const showMoveAssist = ref(false)
const sideTrackPx = ref(null)
const isNarrowLayout = ref(false)

const mqNarrow = '(max-width: 900px)'

function checkNarrow() {
  isNarrowLayout.value =
    typeof window !== 'undefined' && window.matchMedia(mqNarrow).matches
}

function measureSideTrack() {
  checkNarrow()
  if (isNarrowLayout.value) {
    sideTrackPx.value = null
    return
  }
  const el = boardWrapRef.value
  if (!el) return
  const h = Math.round(el.getBoundingClientRect().height)
  if (h > 0) {
    sideTrackPx.value = h
  }
}

const sideTrackStyle = computed(() => {
  if (isNarrowLayout.value || sideTrackPx.value == null) {
    return {}
  }
  return { height: `${sideTrackPx.value + 176}px` }
})

let resizeObserver = null
let mqListener = null

onMounted(() => {
  checkNarrow()
  mqListener = () => measureSideTrack()
  window.matchMedia(mqNarrow).addEventListener('change', mqListener)
  window.addEventListener('resize', measureSideTrack)

  nextTick(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => measureSideTrack())
    })
    const el = boardWrapRef.value
    if (el && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => measureSideTrack())
      resizeObserver.observe(el)
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('resize', measureSideTrack)
  if (typeof window !== 'undefined' && mqListener) {
    window.matchMedia(mqNarrow).removeEventListener('change', mqListener)
  }
  resizeObserver?.disconnect()
})

// 使用游戏状态管理
const {
  currentPlayer,
  gameMode,
  playerNameBlack,
  playerNameWhite,
  displayNameBlack,
  displayNameWhite,
  gameOver,
  winner,
  skipMessage,
  aiThinking,
  scores,
  validMoveSet,
  moveAssistMap,
  canInteract,
  statusBannerRich,
  resultTitle,
  moveLog,
  replayCursor,
  localSaves,
  displayBoard,
  displayPieceMetas,
  displayLastMoveIndex,
  initGame,
  handlePlayerMove,
  makeAIMove,
  switchMode,
  setReplayStep,
  downloadGameRecord,
  importGameFromJson,
  saveCurrentToLocalStorage,
  loadSavedFromLocal,
  removeSavedFromLocal
} = useGameState()

function onLocaleChange(ev) {
  setLocale(ev.target.value)
}

watch(
  locale,
  () => {
    if (typeof document !== 'undefined') {
      document.title = t('meta.pageTitle')
    }
  },
  { immediate: true }
)

// 监听当前玩家变化，触发AI下棋
watch(currentPlayer, (newPlayer) => {
  if (gameMode.value === 'pvc' && newPlayer === 2 && !gameOver.value) {
    aiThinking.value = true
    // 延迟模拟AI思考时间
    setTimeout(() => {
      makeAIMove()
    }, 450)
  }
})

// 处理棋盘点击
function handleCellClick(index) {
  handlePlayerMove(index)
}

function onReplayPrev() {
  setReplayStep(replayCursor.value - 1)
}

function onReplayNext() {
  setReplayStep(replayCursor.value + 1)
}

function onPickImportFile() {
  importFileInput.value?.click()
}

async function onImportGameFile(ev) {
  const input = ev.target
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  try {
    const text = await file.text()
    importGameFromJson(text)
    window.alert(t('side.importOk'))
    await nextTick()
    measureSideTrack()
  } catch (err) {
    window.alert(`${t('side.importFailed')}${err?.message || err}`)
  }
}

function onSaveLocal() {
  try {
    saveCurrentToLocalStorage()
    window.alert(t('side.saveOk'))
  } catch (err) {
    window.alert(
      err?.message === 'QUOTA' ? t('side.saveQuota') : err?.message || String(err)
    )
  }
}

async function onLoadLocalSave(id) {
  try {
    loadSavedFromLocal(id)
    window.alert(t('side.importOk'))
  } catch (err) {
    window.alert(`${t('side.importFailed')}${err?.message || err}`)
  }
}

function onRemoveLocalSave(id) {
  if (!window.confirm(t('side.confirmDeleteSave'))) return
  removeSavedFromLocal(id)
}

// 组件挂载时初始化游戏
onMounted(() => {
  initGame()
})
</script>

<style scoped>
.app-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: min(960px, 100%);
}

.app-main {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 18px;
  width: 100%;
  min-width: 0;
  max-width: min(820px, 100%);
}

.app-footer {
  margin-top: 10px;
  padding: 8px 10px 16px;
  text-align: center;
  color: var(--text-gray, #94a3b8);
  font-size: 0.74rem;
  opacity: 0.9;
}

.app-header {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.title-row {
  width: 100%;
  max-width: min(820px, 100%);
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
}

.game-title {
  grid-column: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 10px;
  margin: 0;
  font-size: clamp(1.35rem, 4vw, 1.85rem);
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--accent-primary, #22c55e);
}

.title-brand {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.28em 0.4em;
}

.title-cn {
  display: inline-flex;
  align-items: center;
}

.title-sep {
  font-weight: 500;
  opacity: 0.55;
  user-select: none;
}

.title-en {
  font-weight: 700;
  letter-spacing: 0.06em;
}

.title-version {
  font-size: 0.72em;
  font-weight: 600;
  color: var(--text-gray, #94a3b8);
  margin-left: 4px;
}

.help-q {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15em;
  height: 1.15em;
  padding: 0;
  margin: 0 1px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  border-radius: 50%;
  font-size: 0.55em;
  line-height: 1;
  font-weight: 700;
  color: var(--text-gray, #94a3b8);
  background: rgba(15, 23, 42, 0.5);
  cursor: pointer;
  vertical-align: middle;
  transition:
    color 0.12s,
    border-color 0.12s,
    background 0.12s;
}

.help-q:hover {
  color: var(--accent-primary, #22c55e);
  border-color: rgba(34, 197, 94, 0.65);
  background: rgba(34, 197, 94, 0.12);
}

.controls-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 14px;
  width: 100%;
  max-width: min(480px, 100%);
}

.controls-row__modes {
  flex-shrink: 0;
}

.assist-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  color: var(--text-gray, #94a3b8);
  user-select: none;
}

.assist-toggle__input {
  width: 14px;
  height: 14px;
  accent-color: var(--accent-primary, #22c55e);
}

.lang-select {
  font: inherit;
  font-size: 0.88rem;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: #1f2937;
  color: var(--text-primary, #f1f5f9);
  cursor: pointer;
}

.title-lang-select {
  grid-column: 3;
  justify-self: end;
  flex: 0 0 auto;
  min-width: 112px;
  margin-right: 26px;
  max-width: 140px;
}

.board-width-line {
  width: min(480px, 100%);
  max-width: min(480px, 100%);
}

.about-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(3px);
}

.about-dialog {
  max-width: 420px;
  width: 100%;
  padding: 20px 22px;
  border-radius: 12px;
  background: #1f2937;
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
}

.about-h {
  margin: 0 0 12px;
  font-size: 1.1rem;
  color: var(--accent-primary, #22c55e);
  text-align: center;
}

.about-body {
  margin: 0 0 18px;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--text-primary, #e2e8f0);
}

.about-close {
  display: block;
  padding: 8px 20px;
  background: var(--accent-primary, #22c55e);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  margin: 0 auto;
}

.about-close:hover {
  opacity: 0.9;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.board-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 14px;
  width: 100%;
  min-width: 0;
}

.board-wrap {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  max-width: min(480px, 100%);
  aspect-ratio: 1;
}

.side-track {
  display: flex;
  flex-direction: column;
  flex: 0 0 clamp(220px, 31vw, 300px);
  width: clamp(220px, 31vw, 300px);
  max-width: clamp(220px, 31vw, 300px);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  margin-top: -176px;
}

.side-track.side-track--mobile {
  flex: none;
  width: min(480px, 100%);
  max-width: 100%;
  max-height: none;
  overflow: visible;
}

@media (max-width: 900px) {
  .title-row {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 8px;
  }

  .game-title {
    grid-column: 1;
    justify-content: flex-start;
    flex-wrap: nowrap;
    font-size: clamp(1.1rem, 5vw, 1.45rem);
    gap: 4px 6px;
    min-width: 0;
  }

  .title-brand {
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  .title-lang-select {
    grid-column: 2;
    justify-self: end;
    margin-right: 0;
    min-width: 92px;
    max-width: 122px;
    padding: 5px 8px;
    font-size: 0.78rem;
  }

  .controls-row {
    justify-content: center;
    flex-wrap: nowrap;
    gap: 6px 8px;
    max-width: min(480px, 100%);
    overflow: hidden;
  }

  .assist-toggle {
    font-size: 0.76rem;
    gap: 4px;
    white-space: nowrap;
  }

  .assist-toggle__input {
    width: 12px;
    height: 12px;
  }

  .board-row {
    flex-direction: column;
    align-items: center;
  }

  .board-wrap {
    flex: none;
    width: min(480px, 100%);
    max-width: 100%;
  }

  .side-track {
    margin-top: 0;
    height: auto;
    width: 100%;
    max-width: min(480px, 100%);
    flex: none;
  }
}

/* 状态文本高亮 */
.status-block {
  min-height: 44px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  text-align: center;
}

.status-replay-nav {
  display: flex;
  gap: 10px;
  margin-top: 6px;
}

.status-replay-btn {
  flex: 1;
  min-height: 42px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.5);
  color: var(--text-white, #f1f5f9);
  font-size: 1.25rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.12s, opacity 0.12s;
}

.status-replay-btn:hover:not(:disabled) {
  background: rgba(34, 197, 94, 0.18);
  border-color: rgba(34, 197, 94, 0.5);
}

.status-replay-btn:disabled {
  opacity: 0.32;
  cursor: not-allowed;
}

@media (max-width: 900px) {
  .status-block {
    min-height: 24px;
    gap: 0;
    line-height: 1.2;
  }

  .status-text,
  .skip-message {
    min-height: 0;
    font-size: 0.9rem;
  }

  .status-replay-btn {
    min-height: 38px;
  }
}

.status-text,
.skip-message {
  min-height: 20px;
}

.highlight {
  font-weight: 600;
  color: var(--accent-primary);
}

.status-emph {
  font-weight: 800;
  color: #b91c1c;
}
</style>